<template>
    <div class="report-table">
        <notification-container :notifications="notifications" @dismiss="dismissNotification" />

        <!-- Views tab bar (hidden for public shared reports) -->
        <div v-if="!isPublic" class="report-table__tabs" @click.stop>
                <a
                    href="#"
                    class="report-table__tab"
                    :class="{ 'report-table__tab--active': activeViewId === null }"
                    @click.prevent="applyDefaultView"
                >All</a>
                <template v-if="viewsLoading">
                    <span class="report-table__tab-skeleton"></span>
                    <span class="report-table__tab-skeleton report-table__tab-skeleton--sm"></span>
                </template>
                <template v-else>
                    <a
                        v-for="view in savedViews"
                        :key="view.id"
                        href="#"
                        class="report-table__tab report-table__tab--loaded"
                        :class="{ 'report-table__tab--active': activeViewId === view.id }"
                        @click.prevent="applyView(view)"
                    >
                        {{ view.name }}
                        <span v-if="view.is_preset" class="report-table__tab-shared" title="Built-in view — use New view to save an editable copy"><i class="fas fa-lock"></i></span>
                        <span v-else-if="view.user_id === null" class="report-table__tab-shared" title="Shared view"><i class="fas fa-users"></i></span>
                        <template v-if="!view.is_preset">
                            <span
                                class="report-table__tab-pin"
                                :class="{ 'report-table__tab-pin--active': view.is_pinned }"
                                :title="view.is_pinned ? 'Unpin default view' : 'Pin as default view'"
                                @click.stop.prevent="togglePinView(view)"
                            ><i class="fas fa-thumbtack"></i></span>
                            <span class="report-table__tab-close" @click.stop.prevent="deleteView(view.id)">×</span>
                        </template>
                    </a>
                </template>
                <span v-if="showSaveForm" class="report-table__save-form">
                    <input
                        ref="viewNameInput"
                        type="text"
                        class="report-table__tab-name-input"
                        v-model="newViewName"
                        placeholder="View name…"
                        @keyup.enter="confirmSaveView"
                        @keyup.esc="cancelSaveView"
                    />
                    <label class="report-table__share-label">
                        <input type="checkbox" v-model="shareView" />
                        Share with all users
                    </label>
                    <button
                        type="button"
                        class="report-table__tab-save-btn"
                        :disabled="!newViewName.trim() || savingView"
                        @click="confirmSaveView"
                    >
                        <i v-if="savingView" class="fas fa-spinner fa-spin"></i>
                        <span v-else>Save</span>
                    </button>
                    <a href="#" class="report-table__tab-cancel" @click.prevent="cancelSaveView">×</a>
                </span>
                <button v-else type="button" class="report-table__new-view-btn" @click.stop="openSaveForm">
                    <i class="fas fa-plus" style="font-size: 9px;"></i> New view
                </button>
                <div v-if="$slots['tabs-right']" class="report-table__tabs-right">
                    <slot name="tabs-right"></slot>
                </div>
            </div>

        <div class="report-table__toolbar">
            <div class="report-table__search-group">
                <div class="report-table__search-wrap" v-if="hasSearch">
                    <i class="fas fa-search report-table__search-icon"></i>
                    <input
                        type="text"
                        class="report-table__search-input"
                        :value="search"
                        placeholder="Search…"
                        @input="onSearchInput($event.target.value)"
                    />
                    <a v-if="search" href="#" class="report-table__search-clear" @click.prevent="clearSearch">×</a>
                    <span v-if="!search" class="report-table__search-count">{{ pagination.count }} records</span>
                </div>
                <span v-else class="report-table__count">{{ pagination.count }} Records</span>

                <!-- Save changes — only visible when on a named view with unsaved changes -->
                <button
                    v-if="!isPublic && activeViewId !== null && viewDirty && !activeViewIsPreset"
                    type="button"
                    class="btn btn-sm btn-primary report-table__save-changes-btn"
                    :disabled="updatingView"
                    @click="updateView"
                >
                    <i v-if="updatingView" class="fas fa-spinner fa-spin"></i>
                    <span v-else>Save changes</span>
                </button>
            </div>

            <div class="report-table__tools">
                <slot name="toolbar"></slot>

                <!-- Column picker -->
                <div class="btn-group" style="position: relative; margin-left: 4px;">
                    <button
                        type="button"
                        class="btn btn-sm btn-default"
                        @click.stop="toggleColumnPicker"
                        :title="'Columns'"
                    >
                        <i class="fas fa-columns"></i> <span class="report-table__btn-label">Columns</span>
                        <span v-if="totalPickerFields > 0" class="report-table__col-count">{{ activeFields.length }}/{{ totalPickerFields }}</span>
                        <i class="fas fa-chevron-down report-table__btn-label" style="font-size: 10px;"></i>
                    </button>
                    <div v-if="showColumnPicker" class="dropdown-menu dropdown-menu-right report-table__picker-dropdown" @click.stop>
                        <div class="report-table__picker-search-wrap">
                            <input
                                ref="columnSearchInput"
                                type="text"
                                class="report-table__picker-search"
                                v-model="columnSearch"
                                placeholder="Search columns…"
                                @click.stop
                            />
                        </div>
                        <ul class="report-table__picker-list">
                            <li class="dropdown-header">Visible columns</li>
                            <li
                                v-for="key in filteredActiveFields"
                                :key="key"
                                class="report-table__col-item"
                                :class="{
                                    'report-table__col-item--dragging':  dragSrcKey === key,
                                    'report-table__col-item--drag-over': dragOverKey === key && dragSrcKey !== key,
                                }"
                                draggable="true"
                                @dragstart="onColumnDragStart(key, $event)"
                                @dragover.prevent="onColumnDragOver(key)"
                                @dragend="onColumnDragEnd"
                            >
                                <a href="#" class="report-table__dropdown-item" draggable="false" @click.prevent="toggleField(key)" :title="availableFields[key] ? availableFields[key].label : key">
                                    <i class="fas fa-grip-vertical report-table__drag-handle"></i>
                                    <i class="fas fa-check-square text-primary" style="width: 14px;"></i>
                                    {{ availableFields[key] ? availableFields[key].label : key }}
                                </a>
                            </li>
                            <template v-if="groupedInactiveColumns.length">
                                <template v-for="group in groupedInactiveColumns">
                                    <li class="dropdown-header">{{ group.name }}</li>
                                    <li v-for="key in group.keys" :key="key">
                                        <a href="#" class="report-table__dropdown-item" @click.prevent="toggleField(key)" :title="availableFields[key] ? availableFields[key].label : key">
                                            <i class="far fa-square text-muted" style="width: 14px; margin-left: 18px;"></i>
                                            {{ availableFields[key] ? availableFields[key].label : key }}
                                        </a>
                                    </li>
                                </template>
                            </template>
                            <li v-if="!filteredActiveFields.length && !groupedInactiveColumns.length" class="report-table__picker-no-results">No columns match</li>
                        </ul>
                    </div>
                </div>

                <!-- Add filter -->
                <div class="btn-group" style="position: relative; margin-left: 4px;" v-if="addableFilterKeys.length > 0">
                    <button
                        type="button"
                        class="btn btn-sm btn-default"
                        @click.stop="toggleFilterAdd"
                        :title="'Filter'"
                    >
                        <i class="fas fa-filter"></i> <span class="report-table__btn-label">Filter</span>
                        <i class="fas fa-chevron-down report-table__btn-label" style="font-size: 10px;"></i>
                    </button>
                    <div v-if="showFilterAdd" class="dropdown-menu dropdown-menu-right report-table__picker-dropdown" @click.stop>
                        <div class="report-table__picker-search-wrap">
                            <input
                                ref="filterSearchInput"
                                type="text"
                                class="report-table__picker-search"
                                v-model="filterSearch"
                                placeholder="Search filters…"
                                @click.stop
                            />
                        </div>
                        <ul class="report-table__picker-list">
                            <template v-if="groupedAddableFilters.length">
                                <template v-for="group in groupedAddableFilters">
                                    <li class="dropdown-header">{{ group.name }}</li>
                                    <li v-for="key in group.keys" :key="key">
                                        <a href="#" class="report-table__dropdown-item" @click.prevent="addFilter(key)">
                                            <i :class="filterTypeIcon(key)" class="report-table__filter-type-icon"></i>
                                            {{ availableFilters[key].label }}
                                        </a>
                                    </li>
                                </template>
                            </template>
                            <li v-if="!groupedAddableFilters.length" class="report-table__picker-no-results">No filters match</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Filter bar -->
        <div v-if="hasFilterBar || hasLockedFilters" class="report-table__filter-bar" @click.stop="showMultiSelectKey = null">
                <!-- Skeleton chips while first load -->
                <template v-if="loading && !filtersReady">
                    <span class="report-table__filter-chip-skeleton"></span>
                    <span class="report-table__filter-chip-skeleton report-table__filter-chip-skeleton--sm"></span>
                </template>

                <!-- Locked filters (non-removable, shown with a lock icon) -->
                <span
                    v-if="filtersReady"
                    v-for="key in lockedFilterKeys"
                    :key="'locked_' + key"
                    class="report-table__filter-chip report-table__filter-chip--locked"
                >
                    <i class="fas fa-lock" style="font-size: 10px; color: #aaa; margin-right: 3px;"></i>
                    <span class="report-table__filter-label">{{ filterLabel(key) }}</span>
                    <span v-if="filterOperators(key).length > 1" class="report-table__op-label">{{ formatOp(lockedFilters[key] && lockedFilters[key].op || '') }}</span>
                    <span class="report-table__filter-locked-value" :title="lockedFilterDisplay(key)">{{ lockedFilterDisplay(key) }}</span>
                </span>

                <!-- Active filters -->
                <span v-if="filtersReady" v-for="key in activeFilterKeys" :key="key" class="report-table__filter-chip">
                    <span class="report-table__filter-label" :title="filterLabel(key)">{{ filterLabel(key) }}</span>

                    <!-- Operator selector (hidden when only one operator) -->
                    <select
                        v-if="filterOperators(key).length > 1"
                        class="report-table__op-select"
                        :value="activeFilterOp(key)"
                        @change="onOperatorChange(key, $event.target.value)"
                    >
                        <option v-for="op in filterOperators(key)" :key="op" :value="op">{{ formatOp(op) }}</option>
                    </select>

                    <!-- Select or handler(select) -->
                    <select
                        v-if="filterUiType(key) === 'select'"
                        class="report-table__filter-input"
                        :value="activeFilterV(key)"
                        @change="onFilterChange(key, $event.target.value)"
                    >
                        <option value="">— Any —</option>
                        <option v-for="opt in filterOptions(key)" :key="opt.val" :value="opt.val">{{ opt.label }}</option>
                    </select>

                    <!-- Text -->
                    <input
                        v-else-if="filterUiType(key) === 'text' && activeFilterOp(key) !== 'is_blank' && activeFilterOp(key) !== 'is_not_blank'"
                        type="text"
                        class="report-table__filter-input"
                        :value="activeFilterV(key)"
                        @input="onFilterTextInput(key, $event.target.value)"
                    />

                    <!-- Date range -->
                    <template v-else-if="filterUiType(key) === 'date_range'">
                        <!-- Relative: within the last/next N days, more than N days ago/ahead -->
                        <template v-if="isRelativeDateOp(activeFilterOp(key))">
                            <input
                                type="number"
                                min="1"
                                class="report-table__filter-input report-table__filter-input--number"
                                :value="activeFilterV(key) && activeFilterV(key).from"
                                @change="onDateChange(key, 'from', $event.target.value)"
                                placeholder="days"
                            />
                            <span class="report-table__filter-sep">days</span>
                        </template>
                        <template v-else-if="activeFilterOp(key) !== 'is_blank' && activeFilterOp(key) !== 'is_not_blank'">
                            <input
                                type="date"
                                class="report-table__filter-input report-table__filter-input--date"
                                :value="activeFilterV(key) && activeFilterV(key).from"
                                @change="onDateChange(key, 'from', $event.target.value)"
                            />
                            <template v-if="activeFilterOp(key) === 'is_between'">
                                <span class="report-table__filter-sep">–</span>
                                <input
                                    type="date"
                                    class="report-table__filter-input report-table__filter-input--date"
                                    :value="activeFilterV(key) && activeFilterV(key).to"
                                    @change="onDateChange(key, 'to', $event.target.value)"
                                />
                            </template>
                        </template>
                    </template>

                    <!-- Numeric -->
                    <template v-else-if="filterUiType(key) === 'numeric'">
                        <input
                            type="number"
                            class="report-table__filter-input report-table__filter-input--number"
                            :value="activeFilterV(key) && activeFilterV(key).from"
                            @change="onNumericChange(key, 'from', $event.target.value)"
                            placeholder="…"
                        />
                        <template v-if="activeFilterOp(key) === 'between'">
                            <span class="report-table__filter-sep">–</span>
                            <input
                                type="number"
                                class="report-table__filter-input report-table__filter-input--number"
                                :value="activeFilterV(key) && activeFilterV(key).to"
                                @change="onNumericChange(key, 'to', $event.target.value)"
                                placeholder="…"
                            />
                        </template>
                    </template>

                    <!-- Boolean -->
                    <select
                        v-else-if="filterUiType(key) === 'boolean'"
                        class="report-table__filter-input"
                        :value="activeFilterV(key)"
                        @change="onFilterChange(key, $event.target.value)"
                    >
                        <option value="">— Any —</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select>

                    <!-- Multi-select (tags + dropdown) -->
                    <template v-else-if="filterUiType(key) === 'multi_select'">
                        <div style="position: relative; display: inline-flex; align-items: center; flex-wrap: wrap; gap: 3px;">
                            <span
                                v-for="opt in selectedMultiOptions(key)"
                                :key="opt.val"
                                class="report-table__tag"
                            >
                                {{ opt.label }}
                                <a href="#" class="report-table__tag-remove" @click.prevent="onMultiSelectChange(key, opt.val, false)">×</a>
                            </span>
                            <button
                                type="button"
                                class="report-table__multi-toggle"
                                @click.stop="toggleMultiSelect(key)"
                            >
                                <span v-if="!selectedMultiOptions(key).length" style="margin-right: 2px; color: #aaa;">—</span>
                                <i class="fas fa-chevron-down" style="font-size: 9px;"></i>
                            </button>
                            <div
                                v-if="showMultiSelectKey === key"
                                class="dropdown-menu report-table__picker-dropdown"
                                style="display: block; top: 100%; left: 0; min-width: 220px;"
                                @click.stop
                            >
                                <div class="report-table__picker-search-wrap">
                                    <input
                                        type="text"
                                        class="report-table__picker-search"
                                        v-model="multiSelectSearch"
                                        placeholder="Search…"
                                        @click.stop
                                    />
                                </div>
                                <ul class="report-table__picker-list">
                                    <template v-if="filteredPinnedOptions(key).length > 0">
                                        <li v-for="opt in filteredPinnedOptions(key)" :key="opt.val">
                                            <label class="report-table__multi-option">
                                                <input
                                                    type="checkbox"
                                                    :value="opt.val"
                                                    :checked="isMultiSelected(key, opt.val)"
                                                    @change="onMultiSelectChange(key, opt.val, $event.target.checked)"
                                                />
                                                {{ opt.label }}
                                            </label>
                                        </li>
                                        <li v-if="filterGroups(key).length > 0 || filteredRegularOptions(key).length > 0" role="separator" class="divider"></li>
                                    </template>
                                    <template v-if="filterGroups(key).length > 0">
                                        <li v-for="opt in filteredUngroupedOptions(key)" :key="opt.val">
                                            <label class="report-table__multi-option">
                                                <input
                                                    type="checkbox"
                                                    :value="opt.val"
                                                    :checked="isMultiSelected(key, opt.val)"
                                                    @change="onMultiSelectChange(key, opt.val, $event.target.checked)"
                                                />
                                                {{ opt.label }}
                                            </label>
                                        </li>
                                        <template v-for="group in filterGroups(key)">
                                            <li v-if="group.label" class="dropdown-header">{{ group.label }}</li>
                                            <li v-for="opt in group.options" :key="opt.val">
                                                <label class="report-table__multi-option">
                                                    <input
                                                        type="checkbox"
                                                        :value="opt.val"
                                                        :checked="isMultiSelected(key, opt.val)"
                                                        @change="onMultiSelectChange(key, opt.val, $event.target.checked)"
                                                    />
                                                    {{ opt.label }}
                                                </label>
                                            </li>
                                        </template>
                                    </template>
                                    <template v-else>
                                        <li v-for="opt in filteredRegularOptions(key)" :key="opt.val">
                                            <label class="report-table__multi-option">
                                                <input
                                                    type="checkbox"
                                                    :value="opt.val"
                                                    :checked="isMultiSelected(key, opt.val)"
                                                    @change="onMultiSelectChange(key, opt.val, $event.target.checked)"
                                                />
                                                {{ opt.label }}
                                            </label>
                                        </li>
                                    </template>
                                    <li v-if="filterGroups(key).length === 0 && filteredPinnedOptions(key).length === 0 && filteredRegularOptions(key).length === 0" class="report-table__picker-no-results">No options match</li>
                                </ul>
                            </div>
                            <!--
                                Relative, and part of the criterion rather than a filter beside it:
                                "did a half marathon in the last 12 months" must be satisfied by one
                                entry, and a stored duration keeps meaning the same thing next year
                                where a fixed year would not (EVT-2718).
                            -->
                            <template v-if="supportsWindow(key)">
                                <span class="report-table__filter-sep">in the last</span>
                                <input
                                    type="number"
                                    min="1"
                                    class="report-table__filter-input report-table__filter-input--number"
                                    :value="filterWindow(key)"
                                    placeholder="any time"
                                    @change="onWindowChange(key, $event.target.value)"
                                />
                                <span class="report-table__filter-sep">months</span>
                            </template>
                        </div>
                    </template>

                    <a v-if="pinnedFilters.indexOf(key) === -1" href="#" class="report-table__filter-remove" @click.prevent="removeFilter(key)">×</a>
                </span>

                <a
                    v-if="hasActiveFilters"
                    href="#"
                    class="report-table__clear-all"
                    @click.prevent="applyDefaultView"
                >Reset</a>

                <!--
                    The number at the end of a chain of criteria does not say which criterion cost
                    the people, which is the question asked as soon as it comes back smaller than
                    expected. A query per criterion, so it is fetched only when asked for (EVT-2718).
                -->
                <span v-if="hasActiveFilters" class="report-table__filter-summary-wrap">
                    <a
                        href="#"
                        class="report-table__filter-summary-toggle"
                        :class="{ 'report-table__filter-summary-toggle--open': filterSummaryOpen }"
                        @click.prevent="toggleFilterSummary"
                    ><i class="fas fa-filter"></i> Filter summary</a>

                    <div v-if="filterSummaryOpen" class="report-table__filter-summary" @click.stop>
                        <div class="report-table__filter-summary-head">
                            <span>Filter summary</span>
                            <a href="#" class="report-table__filter-summary-close" @click.prevent="filterSummaryOpen = false">×</a>
                        </div>

                        <div v-if="filterSummaryLoading" class="report-table__filter-summary-note">
                            <i class="fas fa-spinner fa-spin"></i> Counting each step&hellip;
                        </div>

                        <div v-else-if="filterSummaryError" class="report-table__filter-summary-note">
                            That didn&rsquo;t load.
                            <a href="#" @click.prevent="fetchFilterSummary">Try again</a>
                        </div>

                        <template v-else-if="filterSummary.length">
                            <div
                                v-for="(step, index) in filterSummary"
                                :key="step.key || '__all__'"
                                class="report-table__filter-summary-step"
                                :class="{ 'report-table__filter-summary-step--stale': filterSummaryStale }"
                            >
                                <div class="report-table__filter-summary-line">
                                    <span class="report-table__filter-summary-label">{{ step.label }}</span>
                                    <span v-if="index > 0" class="report-table__filter-summary-drop">{{ stepDrop(index) }}</span>
                                    <span class="report-table__filter-summary-count">{{ step.count }}</span>
                                </div>
                                <div class="report-table__filter-summary-track">
                                    <div class="report-table__filter-summary-fill" :style="{ width: stepWidth(index) }"></div>
                                </div>
                            </div>

                            <div v-if="filterSummaryStale" class="report-table__filter-summary-note">
                                Your criteria have changed since this was worked out.
                                <a href="#" @click.prevent="fetchFilterSummary">Update it</a>
                            </div>
                        </template>

                        <div v-else class="report-table__filter-summary-note">
                            Too many filters to summarise usefully.
                        </div>
                    </div>
                </span>
            </div>

        <!-- Bulk actions toolbar -->
        <div v-if="showRowCheckboxes && selectedIds.length > 0" class="report-table__bulk-bar" @click.stop>
            <slot name="bulk-actions" :selected-ids="selectedIds" :clear-selected="clearSelected" :total-count="pagination.count" :page-row-count="rows.length"></slot>
        </div>

        <div class="report-table__body">
            <div v-if="loading" class="text-center" style="padding: 40px 0;">
                    <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
                </div>

                <div v-else class="table-responsive">
                    <table class="table table-striped table-bordered table-hover" style="margin-bottom: 0;">
                        <thead>
                            <tr>
                                <th v-if="showRowCheckboxes" class="report-table__checkbox-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        :checked="allOnPageSelected"
                                        :indeterminate.prop="someOnPageSelected"
                                        @change="toggleSelectAll($event.target.checked)"
                                    />
                                </th>
                                <th
                                    v-for="key in allActiveFields"
                                    :key="key"
                                    class="report-table__th"
                                    :title="fieldLabel(key)"
                                    @click="toggleSort(key)"
                                >
                                    {{ fieldLabel(key) }}
                                    <i
                                        v-if="sortBy === key && !sortDesc"
                                        class="fas fa-chevron-up report-table__sort-icon report-table__sort-icon--active"
                                    ></i>
                                    <i
                                        v-else-if="sortBy === key && sortDesc"
                                        class="fas fa-chevron-down report-table__sort-icon report-table__sort-icon--active"
                                    ></i>
                                    <i
                                        v-else
                                        class="fas fa-sort report-table__sort-icon"
                                    ></i>
                                </th>
                                <th v-if="hasRowActions" style="width: 1%; white-space: nowrap;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="rows.length === 0 && !$scopedSlots['prepend-rows']">
                                <td
                                    :colspan="totalColumns"
                                    class="text-center text-muted"
                                    style="padding: 30px;"
                                >
                                    No records found.
                                </td>
                            </tr>
                            <slot name="prepend-rows" :active-fields="allActiveFields" :has-row-actions="hasRowActions"></slot>
                            <tr v-for="row in rows" :key="row.id">
                                <td v-if="showRowCheckboxes" class="report-table__checkbox-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        :value="row.id"
                                        :checked="selectedIds.indexOf(row.id) !== -1"
                                        @change="toggleSelectRow(row.id, $event.target.checked)"
                                    />
                                </td>
                                <td v-for="key in allActiveFields" :key="key">
                                    <span v-if="availableFields[key] && availableFields[key].badge_map"
                                          class="badge"
                                          :class="availableFields[key].badge_map[row[key]] || 'badge-secondary'">{{ formatCell(key, row[key], row) }}</span>
                                    <span v-else-if="availableFields[key] && availableFields[key].format === 'html'"
                                          v-html="row[key]"></span>
                                    <template v-else>{{ formatCell(key, row[key], row) }}</template>
                                </td>
                                <td v-if="hasRowActions" style="white-space: nowrap;">
                                    <slot name="row-actions" :item="row"></slot>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        <div
            v-if="!loading"
            class="report-table__footer"
            :class="{ 'report-table__footer--fixed': footerFixed }"
            :style="footerFixed ? { left: footerLeft + 'px', width: footerWidth + 'px' } : {}"
        >
            <small class="text-muted report-table__footer-count">
                Showing {{ rowRangeStart }}–{{ rowRangeEnd }} of {{ pagination.count }}
            </small>
            <nav class="report-table__footer-nav">
                <ul class="pagination pagination-sm" style="margin: 0;">
                    <li class="page-item" :class="{ disabled: page <= 1 }">
                        <a href="#" class="page-link" @click.prevent="goToPage(page - 1)">
                            <i class="fas fa-chevron-left"></i>
                        </a>
                    </li>
                    <li
                        v-for="(p, i) in visiblePages"
                        :key="i"
                        class="page-item"
                        :class="{ active: p === page, disabled: p === '...' }"
                    >
                        <a href="#" class="page-link" @click.prevent="p !== '...' && goToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: page >= pagination.pages }">
                        <a href="#" class="page-link" @click.prevent="goToPage(page + 1)">
                            <i class="fas fa-chevron-right"></i>
                        </a>
                    </li>
                </ul>
            </nav>
            <div class="report-table__footer-limit" style="position: relative;">
                <button type="button" class="report-table__limit-btn" @click.stop="showLimitPicker = !showLimitPicker">
                    {{ limit }} per page <i class="fas fa-chevron-up" style="font-size: 9px;"></i>
                </button>
                <div v-if="showLimitPicker" class="report-table__limit-dropdown" @click.stop>
                    <a
                        v-for="n in limitOptions"
                        :key="n"
                        href="#"
                        class="report-table__limit-option"
                        :class="{ 'report-table__limit-option--active': n === limit }"
                        @click.prevent="changeLimit(n); showLimitPicker = false"
                    >{{ n }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationContainer from '../../../../src/Assets/components/Common/NotificationContainer.vue';
import notificationsMixin from '../../../../src/Assets/mixins/notifications.js';
import operatorLabelsMixin from '../../../../src/Assets/mixins/operatorLabels.js';

export default {
    name: 'ReportTable',

    components: { NotificationContainer },

    mixins: [notificationsMixin, operatorLabelsMixin],

    props: {
        endpoint: {
            required: true,
            type: String,
        },
        entityType: {
            required: true,
            type: String,
        },
        defaultSortBy: {
            default: '',
            required: false,
            type: String,
        },
        defaultSortDesc: {
            default: false,
            required: false,
            type: Boolean,
        },
        rowCheckboxes: {
            default: true,
            required: false,
            type: Boolean,
        },
        defaultFilters: {
            default: function() { return {}; },
            required: false,
            type: Object,
        },
        lockedFields: {
            default: function() { return []; },
            required: false,
            type: Array,
        },
        pinnedFilters: {
            default: function() { return []; },
            required: false,
            type: Array,
        },
        timezone: {
            default: 'UTC',
            required: false,
            type: String,
        },
        isPublic: {
            default: false,
            required: false,
            type: Boolean,
        },
        pinnedView: {
            default: null,
            required: false,
            type: Object,
        },
    },

    data() {
        return {
            loading: false,
            rows: [],
            filterSummary: [],
            filterSummaryOpen: false,
            filterSummaryLoading: false,
            filterSummaryError: false,
            filterSummaryKey: '',
            filterSummarySeq: 0,
            availableFields: {},
            availableFilters: {},
            activeFields: [],
            activeFilters: {},
            search: '',
            lockedFilters: {},
            pagination: { page: 1, pages: 1, count: 0, limit: 50 },
            page: 1,
            limit: 50,
            limitOptions: [50, 100, 150, 200],
            autoSaveTimer: null,
            sortBy: this.defaultSortBy,
            sortDesc: this.defaultSortDesc,
            selectedIds: [],
            savedViews: [],
            viewsLoading: true,
            activeViewId: null,
            activeViewIsPreset: false,
            showSaveForm: false,
            viewDirty: false,
            updatingView: false,
            newViewName: '',
            shareView: false,
            savingView: false,
            defaultFieldKeys: [],
            showColumnPicker: false,
            showFilterAdd: false,
            columnSearch: '',
            filterSearch: '',
            showMultiSelectKey: null,
            multiSelectSearch: '',
            showLimitPicker: false,
            groupOrder: [],
            fetchSeq: 0,
            debounceTimer: null,
            footerFixed: false,
            footerLeft: 0,
            footerWidth: 0,
            dragSrcKey: null,
            dragOverKey: null,
            defaultFiltersApplied: false,
        };
    },

    computed: {
        /**
         * Only the things that change the counts. Page, sort and chosen columns all change the
         * query string without changing a single figure in the summary.
         */
        filterSummaryCurrentKey() {
            return JSON.stringify({ f: this.activeFilters, s: this.search });
        },

        filterSummaryStale() {
            return this.filterSummary.length > 0 && this.filterSummaryKey !== this.filterSummaryCurrentKey;
        },

        hasRowActions() {
            return !!this.$scopedSlots['row-actions'];
        },
        hasBulkActions() {
            return !!this.$scopedSlots['bulk-actions'];
        },
        showRowCheckboxes() {
            return this.rowCheckboxes && this.hasBulkActions;
        },
        allOnPageSelected() {
            if (this.rows.length === 0) return false;
            var self = this;
            return this.rows.every(function(row) {
                return self.selectedIds.indexOf(row.id) !== -1;
            });
        },
        someOnPageSelected() {
            if (this.rows.length === 0) return false;
            var self = this;
            var count = this.rows.filter(function(row) {
                return self.selectedIds.indexOf(row.id) !== -1;
            }).length;
            return count > 0 && count < this.rows.length;
        },
        allActiveFields() {
            var self = this;
            // export_only fields ride along in the view so they reach the CSV, but are never drawn
            // as a column — notes_text would otherwise put every note's concatenated text on screen.
            var userChosen = this.activeFields.filter(function(k) {
                var field = self.availableFields[k];
                return self.lockedFields.indexOf(k) === -1 && !(field && field.export_only);
            });
            return this.lockedFields.concat(userChosen);
        },
        totalColumns() {
            return this.allActiveFields.length
                + (this.showRowCheckboxes ? 1 : 0)
                + (this.hasRowActions ? 1 : 0);
        },
        hasSearch() {
            return !!this.availableFilters.search;
        },
        filtersReady() {
            return Object.keys(this.availableFilters).length > 0;
        },
        activeFilterKeys() {
            return Object.keys(this.activeFilters);
        },
        hasActiveFilters() {
            return this.activeFilterKeys.length > 0;
        },
        hasFilterBar() {
            return this.hasActiveFilters || this.hasLockedFilters;
        },
        inactiveColumnKeys() {
            var self = this;
            return Object.keys(this.availableFields).filter(function(k) {
                return self.lockedFields.indexOf(k) === -1
                    && self.activeFields.indexOf(k) === -1
                    && !self.availableFields[k].export_only;
            });
        },
        totalPickerFields() {
            var self = this;
            return Object.keys(this.availableFields).filter(function(k) {
                return self.lockedFields.indexOf(k) === -1 && !self.availableFields[k].export_only;
            }).length;
        },
        filteredActiveFields() {
            var search = this.columnSearch.trim().toLowerCase();
            if (!search) return this.activeFields;
            var self = this;
            return this.activeFields.filter(function(k) {
                var label = self.availableFields[k] ? self.availableFields[k].label.toLowerCase() : k.toLowerCase();
                return label.indexOf(search) !== -1;
            });
        },
        filteredInactiveColumnKeys() {
            var search = this.columnSearch.trim().toLowerCase();
            if (!search) return this.inactiveColumnKeys;
            var self = this;
            return this.inactiveColumnKeys.filter(function(k) {
                var label = self.availableFields[k] ? self.availableFields[k].label.toLowerCase() : k.toLowerCase();
                return label.indexOf(search) !== -1;
            });
        },
        groupedInactiveColumns() {
            var self = this;
            var groupMap = {};
            this.filteredInactiveColumnKeys.forEach(function(k) {
                var group = (self.availableFields[k] && self.availableFields[k].group) ? self.availableFields[k].group : 'Other';
                if (!groupMap[group]) groupMap[group] = [];
                groupMap[group].push(k);
            });
            var order = self.groupOrder.length ? self.groupOrder : Object.keys(groupMap);
            var known = order.filter(function(g) { return groupMap[g]; }).map(function(name) {
                return { name: name, keys: groupMap[name] };
            });
            var unknown = Object.keys(groupMap).filter(function(g) { return order.indexOf(g) === -1; }).map(function(name) {
                return { name: name, keys: groupMap[name] };
            });
            return known.concat(unknown);
        },
        filteredAddableFilterKeys() {
            var search = this.filterSearch.trim().toLowerCase();
            if (!search) return this.addableFilterKeys;
            var self = this;
            return this.addableFilterKeys.filter(function(k) {
                return self.availableFilters[k].label.toLowerCase().indexOf(search) !== -1;
            });
        },
        groupedAddableFilters() {
            var self = this;
            var groupMap = {};
            this.filteredAddableFilterKeys.forEach(function(k) {
                var group = (self.availableFilters[k] && self.availableFilters[k].group) ? self.availableFilters[k].group : 'Other';
                if (!groupMap[group]) groupMap[group] = [];
                groupMap[group].push(k);
            });
            var order = self.groupOrder.length ? self.groupOrder : Object.keys(groupMap);
            var known = order.filter(function(g) { return groupMap[g]; }).map(function(name) {
                return { name: name, keys: groupMap[name] };
            });
            var unknown = Object.keys(groupMap).filter(function(g) { return order.indexOf(g) === -1; }).map(function(name) {
                return { name: name, keys: groupMap[name] };
            });
            return known.concat(unknown);
        },
        addableFilterKeys() {
            var self = this;
            return Object.keys(this.availableFilters).filter(function(k) {
                return k !== 'search'
                    && self.activeFilterKeys.indexOf(k) === -1
                    && !self.lockedFilters.hasOwnProperty(k);
            });
        },
        lockedFilterKeys() {
            return Object.keys(this.lockedFilters);
        },
        hasLockedFilters() {
            return this.lockedFilterKeys.length > 0;
        },
        visiblePages() {
            var total = this.pagination.pages;
            var current = this.page;
            var pages = [];
            if (total <= 7) {
                for (var i = 1; i <= total; i++) {
                    pages.push(i);
                }
                return pages;
            }
            pages.push(1);
            if (current > 3) pages.push('...');
            var start = Math.max(2, current - 1);
            var end = Math.min(total - 1, current + 1);
            for (var j = start; j <= end; j++) {
                pages.push(j);
            }
            if (current < total - 2) pages.push('...');
            pages.push(total);
            return pages;
        },
        rowRangeStart() {
            if (this.pagination.count === 0) return 0;
            return (this.page - 1) * this.pagination.limit + 1;
        },
        rowRangeEnd() {
            return Math.min(this.page * this.pagination.limit, this.pagination.count);
        },
    },

    mounted() {
        this.restoreFromUrl();
        var hasUrlState = window.location.search.indexOf('fields%5B%5D') !== -1
            || window.location.search.indexOf('fields[]') !== -1;
        if (this.pinnedView) {
            this.activeViewId = this.pinnedView.id;
            this.activeViewIsPreset = false;
            if (!hasUrlState) {
                var config = typeof this.pinnedView.config === 'string'
                    ? JSON.parse(this.pinnedView.config)
                    : this.pinnedView.config;
                this.activeFields  = config.fields || [];
                this.activeFilters = this.normalizeFilters(config.filters || {});
                this.sortBy        = config.sort_by || this.defaultSortBy;
                this.sortDesc      = config.sort_desc !== undefined ? !!config.sort_desc : this.defaultSortDesc;
                if (config.limit && this.limitOptions.indexOf(config.limit) !== -1) this.limit = config.limit;
            }
        } else if (Object.keys(this.activeFilters).length === 0 && Object.keys(this.defaultFilters).length > 0) {
            this.activeFilters = this.normalizeFilters(JSON.parse(JSON.stringify(this.defaultFilters)));
        }
        this.fetchData();
        this.loadSavedViews();
        document.addEventListener('click', this.closeDropdowns);
        window.addEventListener('scroll', this.updateFooterFixed, { passive: true });
        window.addEventListener('resize', this.updateFooterFixed, { passive: true });
    },

    beforeDestroy() {
        document.removeEventListener('click', this.closeDropdowns);
        window.removeEventListener('scroll', this.updateFooterFixed);
        window.removeEventListener('resize', this.updateFooterFixed);
        clearTimeout(this.debounceTimer);
        if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    },

    methods: {
        fetchData() {
            this.loading = true;
            this.selectedIds = [];
            var seq = ++this.fetchSeq;
            var qs = this.buildQueryString();
            var url = this.endpoint + (qs ? '?' + qs : '');
            var self = this;

            var requestPromise = this.isPublic
                ? axios.get(url)
                : this.$root.makeApiRequest(function(jwtToken) {
                    return axios.get(url, { headers: { Authorization: jwtToken } });
                });

            requestPromise.then(function(response) {
                if (seq !== self.fetchSeq) return;
                var data = response.data.data;
                self.rows = data.rows || [];
                self.availableFields = data.available_fields || {};
                self.$emit('available-fields-change', self.availableFields);
                self.availableFilters = data.available_filters || {};
                if (data.group_order && data.group_order.length) {
                    self.groupOrder = data.group_order;
                }
                self.lockedFilters = data.locked_filters || {};

                if (!self.defaultFiltersApplied) {
                    self.defaultFiltersApplied = true;
                    if (Object.keys(self.activeFilters).length === 0 && data.default_filters && Object.keys(data.default_filters).length > 0) {
                        self.activeFilters = self.normalizeFilters(data.default_filters);
                        self.fetchData();
                        return;
                    }
                }

                self.pagination = data.pagination || { page: 1, pages: 1, count: 0, limit: 50 };

                if (data.default_fields && data.default_fields.length) {
                    self.defaultFieldKeys = data.default_fields;
                }

                if (self.activeFields.length === 0) {
                    var defaultFields = self.defaultFieldKeys.length
                        ? self.defaultFieldKeys.filter(function(k) { return self.availableFields[k] && self.lockedFields.indexOf(k) === -1; })
                        : Object.keys(self.availableFields).filter(function(k) { return self.availableFields[k].default && self.lockedFields.indexOf(k) === -1; });
                    self.activeFields = defaultFields;
                }
                self.$emit('config-change', self.buildViewConfig());
                self.syncUrl();
                self.$nextTick(function() {
                    requestAnimationFrame(self.updateFooterFixed);
                });
            }).catch(function(error) {
                if (seq !== self.fetchSeq) return;
                console.error('ReportTable fetch error', error);
            }).finally(function() {
                if (seq !== self.fetchSeq) return;
                self.loading = false;
            });
        },

        refresh() {
            this.fetchData();
        },

        toggleFilterSummary() {
            this.filterSummaryOpen = !this.filterSummaryOpen;

            if (this.filterSummaryOpen && (!this.filterSummary.length || this.filterSummaryStale)) {
                this.fetchFilterSummary();
            }
        },

        /**
         * Counts only: the same query string the report uses, so the criteria cannot be serialised
         * one way here and another way there, plus filter_summary=1 to ask for the counts without rows.
         */
        fetchFilterSummary() {
            var url = this.endpoint + '?' + this.buildQueryString() + '&filter_summary=1';
            var key = this.filterSummaryCurrentKey;
            var seq = ++this.filterSummarySeq;
            var self = this;

            this.filterSummaryLoading = true;
            this.filterSummaryError = false;

            var requestPromise = this.isPublic
                ? axios.get(url)
                : this.$root.makeApiRequest(function(jwtToken) {
                    return axios.get(url, { headers: { Authorization: jwtToken } });
                });

            requestPromise.then(function(response) {
                if (seq !== self.filterSummarySeq) return;
                self.filterSummary = response.data.data.filter_summary || [];
                self.filterSummaryKey = key;
            }).catch(function(error) {
                if (seq !== self.filterSummarySeq) return;
                console.error('ReportTable filter summary error', error);
                self.filterSummary = [];
                // Distinct from an empty result: the server returns nothing when it declines to
                // summarise too many filters, and the two need different words.
                self.filterSummaryError = true;
            }).finally(function() {
                if (seq !== self.filterSummarySeq) return;
                self.filterSummaryLoading = false;
            });
        },

        /**
         * What this step cost, against the one before it.
         */
        stepDrop(index) {
            var lost = this.filterSummary[index - 1].count - this.filterSummary[index].count;

            return lost > 0 ? '\u2212' + lost : 'no change';
        },

        /**
         * How much of the unfiltered audience survives to this step. The bars are what make the
         * summary readable at a glance — a column of numbers alone did not.
         */
        stepWidth(index) {
            var baseline = this.filterSummary.length ? this.filterSummary[0].count : 0;
            var count = this.filterSummary[index].count;

            // The floor keeps a small surviving audience visible, but it must not apply to zero:
            // a criterion that eliminated everybody is the one thing the summary exists to show.
            if (!baseline || !count) {
                return '0%';
            }

            return Math.max(1, Math.round((count / baseline) * 100)) + '%';
        },

        buildQueryString() {
            var params = new URLSearchParams();
            params.set('page', this.page);
            params.set('limit', this.limit);

            if (this.sortBy) {
                params.set('sort_by', this.sortBy);
                params.set('sort_desc', this.sortDesc ? '1' : '0');
            }

            if (this.search) {
                params.set('search', this.search);
            }

            for (var i = 0; i < this.allActiveFields.length; i++) {
                params.append('fields[]', this.allActiveFields[i]);
            }

            var filterKeys = Object.keys(this.activeFilters);
            for (var f = 0; f < filterKeys.length; f++) {
                var key = filterKeys[f];
                var entry = this.activeFilters[key];
                if (!entry || entry.op === undefined) continue;

                var op  = entry.op;
                var val = entry.v;

                params.set('filters[' + key + '][op]', op);

                if (val === null || val === undefined || val === '') continue;

                if (val && typeof val === 'object' && Array.isArray(val.values)) {
                    // A criterion carrying its own window: the values and the window travel
                    // together, because the window belongs inside the same EXISTS as the rest of
                    // the criterion. Without this branch the value was dropped from the request
                    // entirely and the filter silently did nothing (EVT-2718).
                    if (!val.values.length) continue;

                    for (var wi = 0; wi < val.values.length; wi++) {
                        params.append('filters[' + key + '][v][values][]', val.values[wi]);
                    }
                    if (val.within_months) {
                        params.set('filters[' + key + '][v][within_months]', val.within_months);
                    }
                } else if (val && typeof val === 'object' && !Array.isArray(val)) {
                    if (val.from) params.set('filters[' + key + '][v][from]', val.from);
                    if (val.to)   params.set('filters[' + key + '][v][to]',   val.to);
                } else if (Array.isArray(val)) {
                    for (var vi = 0; vi < val.length; vi++) {
                        params.append('filters[' + key + '][v][]', val[vi]);
                    }
                } else {
                    params.set('filters[' + key + '][v]', val);
                }
            }

            return params.toString();
        },

        fieldLabel(key) {
            var field = this.availableFields[key];
            return field ? field.label : key;
        },

        formatCell(key, value, row) {
            if (value === null || value === undefined || value === '') return '';
            var field = this.availableFields[key];
            if (!field || !field.format) return value;

            if (field.format === 'label_map' && field.label_map) {
                return field.label_map[value] !== undefined ? field.label_map[value] : value;
            }

            if (field.format === 'boolean') {
                // Checkbox additions are stored as 0/1; show Yes/No rather than the raw number.
                return (value === '0' || value === 0 || value === false) ? 'No' : 'Yes';
            }

            if (field.format === 'currency') {
                var num = parseInt(value, 10);
                if (isNaN(num)) return value;
                var currencyCode = (field.currency_key && row && row[field.currency_key]) ? row[field.currency_key] : 'GBP';
                try {
                    var formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currencyCode });
                    var fraction = formatter.formatToParts(1).find(function(p) { return p.type === 'fraction'; });
                    var decimals = fraction ? fraction.value.length : 2;
                    return formatter.format(num / Math.pow(10, decimals));
                } catch (e) {
                    return (num / 100).toFixed(2) + ' ' + currencyCode;
                }
            }

            if (field.format === 'time') {
                // Time additions are stored as a whole number of seconds; render as hh:mm:ss.
                var totalSeconds = parseInt(value, 10);
                if (isNaN(totalSeconds)) return value;
                var pad = function(n) { return n < 10 ? '0' + n : '' + n; };
                var hours = Math.floor(totalSeconds / 3600);
                var minutes = Math.floor((totalSeconds % 3600) / 60);
                var seconds = totalSeconds % 60;
                return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
            }

            try {
                var date = new Date(value);
                if (isNaN(date.getTime())) return value;

                switch (field.format) {
                    case 'date':
                        return new Intl.DateTimeFormat('en-GB', {
                            timeZone: this.timezone,
                            day:   '2-digit',
                            month: 'short',
                            year:  'numeric',
                        }).format(date);
                    case 'datetime':
                        return new Intl.DateTimeFormat('en-GB', {
                            timeZone: this.timezone,
                            day:    '2-digit',
                            month:  'short',
                            year:   'numeric',
                            hour:   '2-digit',
                            minute: '2-digit',
                        }).format(date);
                    default:
                        return value;
                }
            } catch (e) {
                return value;
            }
        },

        filterLabel(key) {
            var filter = this.availableFilters[key];
            return filter ? filter.label : key;
        },

        lockedFilterDisplay(key) {
            var entry = this.lockedFilters[key];
            if (!entry) return '';
            var val = entry.v;
            if (val === null || val === undefined || val === '') return '';
            var filter = this.availableFilters[key];
            var options = filter ? (filter.options || []) : [];
            var findLabel = function(v) {
                var found = options.filter(function(o) { return o.val === v; })[0];
                return found ? found.label : v;
            };
            if (Array.isArray(val)) {
                return val.map(findLabel).join(', ');
            }
            if (val && typeof val === 'object') {
                var parts = [];
                if (val.from) parts.push(val.from);
                if (val.to) parts.push(val.to);
                return parts.join(' – ') || '';
            }
            return findLabel(String(val));
        },

        filterType(key) {
            var filter = this.availableFilters[key];
            return filter ? filter.type : 'text';
        },

        filterTypeIcon(key) {
            var type = this.filterUiType(key);
            var icons = {
                'multi_select': 'fas fa-check-square',
                'select':       'fas fa-chevron-circle-down',
                'date_range':   'fas fa-calendar-alt',
                'boolean':      'fas fa-toggle-on',
                'numeric':      'fas fa-hashtag',
                'text':         'fas fa-font',
            };
            return icons[type] || 'fas fa-filter';
        },

        filterUiType(key) {
            var filter = this.availableFilters[key];
            if (!filter) return 'text';
            return filter.ui_type || filter.type;
        },

        filterOptions(key) {
            var filter = this.availableFilters[key];
            return filter ? (filter.options || []) : [];
        },

        pinnedFilterOptions(key) {
            var filter = this.availableFilters[key];
            if (!filter || !filter.pinned_options || !filter.pinned_options.length) return [];
            var options = filter.options || [];
            var pinned = filter.pinned_options;
            return options.filter(function(o) { return pinned.indexOf(o.val) !== -1; });
        },

        regularFilterOptions(key) {
            var filter = this.availableFilters[key];
            if (!filter) return [];
            var options = filter.options || [];
            var pinned = filter.pinned_options || [];
            return options.filter(function(o) { return pinned.indexOf(o.val) === -1; });
        },

        filterGroups(key) {
            var filter = this.availableFilters[key];
            if (!filter || !filter.groups || !filter.groups.length) return [];
            var options = filter.options || [];
            var pinned = filter.pinned_options || [];
            var search = this.multiSelectSearch.toLowerCase().trim();
            return filter.groups.map(function(group) {
                return {
                    label: group.label,
                    options: options.filter(function(o) {
                        if (pinned.indexOf(o.val) !== -1 || group.vals.indexOf(o.val) === -1) return false;
                        return !search || o.label.toLowerCase().indexOf(search) !== -1 || group.label.toLowerCase().indexOf(search) !== -1;
                    }),
                };
            }).filter(function(g) { return g.options.length > 0; });
        },

        filteredPinnedOptions(key) {
            var opts = this.pinnedFilterOptions(key);
            var search = this.multiSelectSearch.toLowerCase().trim();
            if (!search) return opts;
            return opts.filter(function(o) { return o.label.toLowerCase().indexOf(search) !== -1; });
        },

        filteredRegularOptions(key) {
            var opts = this.regularFilterOptions(key);
            var search = this.multiSelectSearch.toLowerCase().trim();
            if (!search) return opts;
            return opts.filter(function(o) { return o.label.toLowerCase().indexOf(search) !== -1; });
        },

        filteredUngroupedOptions(key) {
            var filter = this.availableFilters[key];
            if (!filter || !filter.groups || !filter.groups.length) return [];
            var options = filter.options || [];
            var pinned = filter.pinned_options || [];
            var search = this.multiSelectSearch.toLowerCase().trim();
            var groupedVals = [];
            filter.groups.forEach(function(g) { groupedVals = groupedVals.concat(g.vals); });
            return options.filter(function(o) {
                if (pinned.indexOf(o.val) !== -1 || groupedVals.indexOf(o.val) !== -1) return false;
                return !search || o.label.toLowerCase().indexOf(search) !== -1;
            });
        },

        markDirty() {
            if (this.activeViewId === null) return;
            this.viewDirty = true;
            // A preset is code-defined and has no row behind it — leave the change in the table and
            // let the organiser keep it with "New view" if they want it.
            if (this.activeViewIsPreset) return;
            this.scheduleAutoSave();
        },

        scheduleAutoSave() {
            var self = this;
            if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = setTimeout(function() {
                self.autoSaveTimer = null;
                self.updateView();
            }, 2000);
        },

        toggleSort(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = key;
                this.sortDesc = false;
            }
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        goToPage(p) {
            if (p < 1 || p > this.pagination.pages || p === this.page) return;
            this.page = p;
            this.fetchData();
        },

        changeLimit(newLimit) {
            this.limit = newLimit;
            this.page = 1;
            this.markDirty();
            this.fetchData();
        },

        toggleColumnPicker() {
            this.showFilterAdd = false;
            this.filterSearch = '';
            this.showColumnPicker = !this.showColumnPicker;
            if (this.showColumnPicker) {
                this.columnSearch = '';
                this.$nextTick(function() {
                    if (this.$refs.columnSearchInput) this.$refs.columnSearchInput.focus();
                }.bind(this));
            }
        },

        toggleFilterAdd() {
            this.showColumnPicker = false;
            this.columnSearch = '';
            this.showFilterAdd = !this.showFilterAdd;
            if (this.showFilterAdd) {
                this.filterSearch = '';
                this.$nextTick(function() {
                    if (this.$refs.filterSearchInput) this.$refs.filterSearchInput.focus();
                }.bind(this));
            }
        },

        closeDropdowns() {
            this.showColumnPicker = false;
            this.showFilterAdd = false;
            this.showMultiSelectKey = null;
            this.showLimitPicker = false;
            this.filterSummaryOpen = false;
            this.columnSearch = '';
            this.filterSearch = '';
        },

        updateFooterFixed() {
            if (!this.$el) return;
            var rect = this.$el.getBoundingClientRect();
            this.footerFixed = rect.bottom > window.innerHeight;
            this.footerLeft  = rect.left;
            this.footerWidth = rect.width;
        },

        toggleMultiSelect(key) {
            this.showMultiSelectKey = this.showMultiSelectKey === key ? null : key;
            this.multiSelectSearch = '';
            this.showColumnPicker = false;
            this.showFilterAdd = false;
        },

        multiSelectLabel(key) {
            var entry = this.activeFilters[key];
            var selected = entry && Array.isArray(entry.v) ? entry.v : [];
            if (!selected.length) return 'Any';
            var options = this.filterOptions(key);
            return selected.map(function(v) {
                var found = options.filter(function(o) { return o.val === v; })[0];
                return found ? found.label : v;
            }).join(', ');
        },

        selectedMultiOptions(key) {
            var selected = this.multiValues(key);
            if (!selected.length) return [];
            var options = this.filterOptions(key);
            return selected.map(function(v) {
                // Loose match for the same reason the checkbox does: the URL gives back a string
                // where the option list supplies a number, and a strict miss renders the raw id in
                // place of the event's name.
                var found = options.filter(function(o) { return String(o.val) === String(v); })[0];
                return found || { val: v, label: v };
            });
        },

        filterOperators(key) {
            var filter = this.availableFilters[key];
            return filter && filter.operators ? filter.operators : [];
        },



        activeFilterOp(key) {
            var entry = this.activeFilters[key];
            return entry ? entry.op : '';
        },

        isRelativeDateOp(op) {
            return op === 'within_last_days'
                || op === 'more_than_days_ago'
                || op === 'within_next_days'
                || op === 'more_than_days_ahead';
        },

        activeFilterV(key) {
            var entry = this.activeFilters[key];
            return entry ? entry.v : null;
        },

        onColumnDragStart(key, event) {
            this.dragSrcKey = key;
            event.dataTransfer.effectAllowed = 'move';
        },

        onColumnDragOver(key) {
            if (!this.dragSrcKey || this.dragSrcKey === key) return;
            this.dragOverKey = key;
            var fields = this.activeFields.slice();
            var fromIdx = fields.indexOf(this.dragSrcKey);
            var toIdx   = fields.indexOf(key);
            if (fromIdx === -1 || toIdx === -1) return;
            fields.splice(fromIdx, 1);
            fields.splice(toIdx, 0, this.dragSrcKey);
            this.activeFields = fields;
        },

        onColumnDragEnd() {
            this.dragSrcKey  = null;
            this.dragOverKey = null;
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        toggleField(key) {
            var idx = this.activeFields.indexOf(key);
            if (idx === -1) {
                this.activeFields.push(key);
            } else {
                if (this.activeFields.length === 1) return;
                this.activeFields.splice(idx, 1);
            }
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        addFilter(key) {
            var filterDef = this.availableFilters[key];
            if (!filterDef) return;

            var defaultOp = filterDef.operators && filterDef.operators.length ? filterDef.operators[0] : 'in';
            var defaultV  = filterDef.type === 'date_range'   ? { from: '', to: '' }
                          : filterDef.type === 'numeric'      ? { from: '', to: '' }
                          : filterDef.type === 'multi_select' ? []
                          : '';
            this.$set(this.activeFilters, key, { op: defaultOp, v: defaultV });
            this.markDirty();
            this.showFilterAdd = false;

            // A value-less operator (is_blank / is_not_blank) is complete as soon as the filter is
            // added, so apply it immediately. Value-requiring operators wait for onFilterChange.
            if (defaultOp === 'is_blank' || defaultOp === 'is_not_blank') {
                this.page = 1;
                this.fetchData();
            }
        },

        removeFilter(key) {
            if (this.pinnedFilters.indexOf(key) !== -1) return;
            this.$delete(this.activeFilters, key);
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        clearAllFilters() {
            var preserved = {};
            this.pinnedFilters.forEach((key) => {
                if (this.activeFilters[key] !== undefined) {
                    preserved[key] = this.activeFilters[key];
                }
            });
            this.activeFilters = preserved;
            this.search = '';
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        onMultiSelectChange(key, val, checked) {
            var entry   = this.activeFilters[key] || { op: 'in', v: [] };
            var current = this.multiValues(key).slice();
            var idx = -1;
            for (var ci = 0; ci < current.length; ci++) {
                if (String(current[ci]) === String(val)) { idx = ci; break; }
            }
            if (checked && idx === -1) {
                current.push(val);
            } else if (!checked && idx !== -1) {
                current.splice(idx, 1);
            }
            this.$set(this.activeFilters, key, { op: entry.op, v: this.packMultiValue(key, current, this.filterWindow(key)) });
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        /**
         * A participation criterion carries its window inside its value, because the handler that
         * builds the query only receives the value — and the window has to be inside the same
         * EXISTS as the rest of the criterion, or the distance and the recency can be satisfied by
         * two different entries (EVT-2718). Everything else keeps a bare array.
         */
        multiValues(key) {
            var entry = this.activeFilters[key];
            if (!entry) return [];
            if (Array.isArray(entry.v)) return entry.v;
            if (entry.v && Array.isArray(entry.v.values)) return entry.v.values;

            return [];
        },

        /**
         * Whether an option is already chosen. Reads through multiValues so a criterion carrying a
         * window still reports its selections — the value is an object there, and an Array.isArray
         * check rendered every option unchecked. Compared as strings because an id survives the URL
         * round trip as one, while the option list supplies it as a number.
         */
        isMultiSelected(key, val) {
            var selected = this.multiValues(key);

            for (var i = 0; i < selected.length; i++) {
                if (String(selected[i]) === String(val)) return true;
            }

            return false;
        },

        filterWindow(key) {
            var entry = this.activeFilters[key];

            return entry && entry.v && !Array.isArray(entry.v) && entry.v.within_months
                ? entry.v.within_months
                : '';
        },

        supportsWindow(key) {
            var filter = this.availableFilters[key];

            return !!(filter && filter.supports_window);
        },

        packMultiValue(key, values, months) {
            if (!this.supportsWindow(key) || !months) {
                return values;
            }

            return { values: values, within_months: Number(months) };
        },

        onWindowChange(key, months) {
            var entry = this.activeFilters[key] || { op: 'any_of', v: [] };
            this.$set(this.activeFilters, key, {
                op: entry.op,
                v: this.packMultiValue(key, this.multiValues(key), months),
            });
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        onOperatorChange(key, op) {
            var entry = this.activeFilters[key] || {};
            var v = (op === 'is_blank' || op === 'is_not_blank') ? '' : (entry.v !== undefined ? entry.v : '');
            this.$set(this.activeFilters, key, { op: op, v: v });
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        onFilterChange(key, value) {
            var entry = this.activeFilters[key] || {};
            this.$set(this.activeFilters, key, { op: entry.op || 'in', v: value });
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        onFilterTextInput(key, value) {
            var entry = this.activeFilters[key] || {};
            this.$set(this.activeFilters, key, { op: entry.op || 'contains', v: value });
            this.markDirty();
            clearTimeout(this.debounceTimer);
            var self = this;
            this.debounceTimer = setTimeout(function() {
                self.page = 1;
                self.fetchData();
            }, 400);
        },

        onNumericChange(key, part, value) {
            var entry   = this.activeFilters[key] || { op: 'equals', v: { from: '', to: '' } };
            var current = Object.assign({ from: '', to: '' }, entry.v || {});
            current[part] = value;
            this.$set(this.activeFilters, key, { op: entry.op, v: current });
            this.markDirty();
            clearTimeout(this.debounceTimer);
            var self = this;
            this.debounceTimer = setTimeout(function() {
                self.page = 1;
                self.fetchData();
            }, 400);
        },

        onDateChange(key, part, value) {
            var entry   = this.activeFilters[key] || { op: 'is_between', v: { from: '', to: '' } };
            var current = Object.assign({}, entry.v || { from: '', to: '' });
            current[part] = value;
            this.$set(this.activeFilters, key, { op: entry.op, v: current });
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        onSearchInput(value) {
            this.search = value;
            this.markDirty();
            clearTimeout(this.debounceTimer);
            var self = this;
            this.debounceTimer = setTimeout(function() {
                self.page = 1;
                self.fetchData();
            }, 400);
        },

        clearSearch() {
            this.search = '';
            this.markDirty();
            this.page = 1;
            this.fetchData();
        },

        restoreFromUrl() {
            var params = new URLSearchParams(window.location.search);

            var fields = params.getAll('fields[]');
            if (fields.length > 0) {
                this.activeFields = fields;
            }

            var search = params.get('search');
            if (search) this.search = search;

            var sortBy = params.get('sort_by');
            if (sortBy) this.sortBy = sortBy;

            var sortDesc = params.get('sort_desc');
            if (sortDesc !== null) this.sortDesc = sortDesc === '1';

            var page = parseInt(params.get('page'), 10);
            if (page > 1) this.page = page;

            var limit = parseInt(params.get('limit'), 10);
            if (this.limitOptions.indexOf(limit) !== -1) this.limit = limit;

            var filters = {};
            params.forEach(function(value, paramKey) {
                var m = paramKey.match(/^filters\[([^\]]+)\]\[([^\]]+)\](.*)$/);
                if (!m) return;
                var filterKey = m[1];
                var part      = m[2]; // 'op' or 'v'
                var rest      = m[3]; // '', '[]', '[from]', '[to]'

                if (!filters[filterKey]) filters[filterKey] = { op: null, v: null };

                if (part === 'op') {
                    filters[filterKey].op = value;
                } else if (part === 'v') {
                    if (rest === '[]') {
                        if (!Array.isArray(filters[filterKey].v)) filters[filterKey].v = [];
                        filters[filterKey].v.push(value);
                    } else if (rest === '[values][]' || rest === '[within_months]') {
                        // The windowed criterion shape, read back so a shared or reloaded URL
                        // restores the window rather than silently dropping it.
                        if (!filters[filterKey].v || Array.isArray(filters[filterKey].v)) {
                            filters[filterKey].v = { values: [] };
                        }
                        if (rest === '[values][]') {
                            filters[filterKey].v.values.push(value);
                        } else {
                            filters[filterKey].v.within_months = Number(value);
                        }
                    } else if (rest === '[from]' || rest === '[to]') {
                        if (!filters[filterKey].v || typeof filters[filterKey].v !== 'object') {
                            filters[filterKey].v = { from: '', to: '' };
                        }
                        filters[filterKey].v[rest.slice(1, -1)] = value;
                    } else {
                        filters[filterKey].v = value;
                    }
                }
            });
            if (Object.keys(filters).length > 0) {
                this.activeFilters = filters;
            }
        },

        syncUrl() {
            var params = new URLSearchParams();

            if (this.page > 1) params.set('page', this.page);
            if (this.limit !== 50) params.set('limit', this.limit);
            if (this.sortBy) params.set('sort_by', this.sortBy);
            if (this.sortDesc) params.set('sort_desc', '1');
            if (this.search) params.set('search', this.search);

            for (var i = 0; i < this.activeFields.length; i++) {
                params.append('fields[]', this.activeFields[i]);
            }

            var filterKeys = Object.keys(this.activeFilters);
            for (var f = 0; f < filterKeys.length; f++) {
                var key = filterKeys[f];
                var entry = this.activeFilters[key];
                if (!entry || entry.op === undefined) continue;

                params.set('filters[' + key + '][op]', entry.op);

                var val = entry.v;
                if (val === null || val === undefined || val === '') continue;

                if (val && typeof val === 'object' && Array.isArray(val.values)) {
                    // A criterion carrying its own window: the values and the window travel
                    // together, because the window belongs inside the same EXISTS as the rest of
                    // the criterion. Without this branch the value was dropped from the request
                    // entirely and the filter silently did nothing (EVT-2718).
                    if (!val.values.length) continue;

                    for (var wi = 0; wi < val.values.length; wi++) {
                        params.append('filters[' + key + '][v][values][]', val.values[wi]);
                    }
                    if (val.within_months) {
                        params.set('filters[' + key + '][v][within_months]', val.within_months);
                    }
                } else if (val && typeof val === 'object' && !Array.isArray(val)) {
                    if (val.from) params.set('filters[' + key + '][v][from]', val.from);
                    if (val.to)   params.set('filters[' + key + '][v][to]',   val.to);
                } else if (Array.isArray(val)) {
                    for (var v = 0; v < val.length; v++) {
                        params.append('filters[' + key + '][v][]', val[v]);
                    }
                } else {
                    params.set('filters[' + key + '][v]', val);
                }
            }

            var qs = params.toString();
            history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
        },

        loadSavedViews() {
            if (this.isPublic) {
                this.viewsLoading = false;

                return;
            }
            var self = this;
            this.$root.makeApiRequest(function(jwtToken) {
                return axios.get('/api/v0/report_views', {
                    params: { entity_type: self.entityType },
                    headers: { Authorization: jwtToken },
                });
            }).then(function(response) {
                self.savedViews = response.data.data || [];
            }).catch(function(error) {
                console.error('ReportTable load views error', error);
            }).finally(function() {
                self.viewsLoading = false;
            });
        },

        applyView(view) {
            if (this.autoSaveTimer) { clearTimeout(this.autoSaveTimer); this.autoSaveTimer = null; }
            var config = typeof view.config === 'string' ? JSON.parse(view.config) : view.config;
            this.activeFields = config.fields || [];
            this.activeFilters = this.normalizeFilters(config.filters || {});
            this.sortBy = config.sort_by || '';
            this.sortDesc = !!config.sort_desc;
            this.limit = config.limit || 50;
            this.search = '';
            this.activeViewId = view.id;
            this.activeViewIsPreset = !!view.is_preset;
            this.viewDirty = false;
            this.page = 1;
            this.fetchData();
        },

        normalizeFilters(filters) {
            var normalized = {};
            Object.keys(filters).forEach(function(key) {
                var val = filters[key];
                // Already new format
                if (val !== null && typeof val === 'object' && val.hasOwnProperty('op')) {
                    normalized[key] = val;
                } else {
                    // Legacy flat format — wrap with default operator
                    var op = Array.isArray(val) ? 'in'
                           : (val && typeof val === 'object') ? 'is_between'
                           : 'contains';
                    normalized[key] = { op: op, v: val };
                }
            });
            return normalized;
        },

        applyDefaultView() {
            if (this.autoSaveTimer) { clearTimeout(this.autoSaveTimer); this.autoSaveTimer = null; }
            var self = this;
            if (this.defaultFieldKeys.length) {
                this.activeFields = this.defaultFieldKeys.filter(function(k) {
                    return self.availableFields[k] && self.lockedFields.indexOf(k) === -1;
                });
            } else if (Object.keys(this.availableFields).length) {
                this.activeFields = Object.keys(this.availableFields).filter(function(k) {
                    return self.availableFields[k].default && self.lockedFields.indexOf(k) === -1;
                });
            }
            // else: activeFields stays [] and fetchData will populate it from default_fields
            this.activeFilters = Object.keys(this.defaultFilters).length > 0
                ? this.normalizeFilters(JSON.parse(JSON.stringify(this.defaultFilters)))
                : {};
            this.sortBy = this.defaultSortBy;
            this.sortDesc = this.defaultSortDesc;
            this.search = '';
            this.activeViewId = null;
            this.activeViewIsPreset = false;
            this.viewDirty = false;
            this.page = 1;
            this.fetchData();
        },

        buildViewConfig() {
            var self = this;
            var filters = {};
            Object.keys(this.activeFilters).forEach(function(key) {
                var filterDef = self.availableFilters[key];
                if (filterDef && filterDef.unsaveable) return;
                filters[key] = self.activeFilters[key];
            });
            var fieldLabels = {};
            this.activeFields.forEach(function(key) {
                var field = self.availableFields[key];
                if (field) fieldLabels[key] = field.label;
            });
            var filterLabels = {};
            var filterValueLabels = {};
            Object.keys(filters).forEach(function(key) {
                var filterDef = self.availableFilters[key];
                if (!filterDef) return;
                filterLabels[key] = filterDef.label;
                var options = filterDef.options || [];
                var valToLabel = {};
                options.forEach(function(o) { valToLabel[o.val] = o.label; });
                filterValueLabels[key] = valToLabel;
            });
            return {
                fields: this.activeFields.slice(),
                filters: filters,
                sort_by: this.sortBy,
                sort_desc: this.sortDesc,
                limit: this.limit,
                _field_labels: fieldLabels,
                _filter_labels: filterLabels,
                _filter_value_labels: filterValueLabels,
                count: this.pagination.count,
            };
        },

        openSaveForm() {
            this.newViewName = '';
            this.shareView = false;
            this.showSaveForm = true;
            this.$nextTick(function() {
                if (this.$refs.viewNameInput) {
                    this.$refs.viewNameInput.focus();
                }
            }.bind(this));
        },

        cancelSaveView() {
            this.showSaveForm = false;
            this.newViewName = '';
            this.shareView = false;
        },

        confirmSaveView() {
            var name = this.newViewName.trim();
            if (!name || this.savingView) return;

            var self = this;
            this.savingView = true;

            this.$root.makeApiRequest(function(jwtToken) {
                var payload = {
                    entity_type: self.entityType,
                    name: name,
                    config: JSON.stringify(self.buildViewConfig()),
                };
                if (self.shareView) {
                    payload.user_id = null;
                }
                return axios.post('/api/v0/report_views', payload, {
                    headers: { Authorization: jwtToken },
                });
            }).then(function(response) {
                var view = response.data.data;
                self.savedViews.push(view);
                self.activeViewId = view.id;
                self.activeViewIsPreset = false;
                self.showSaveForm = false;
                self.newViewName = '';
            }).catch(function(error) {
                console.error('ReportTable save view error', error);
                self.showAlertMessage('Could not save view. Please try again.', 'error');
            }).finally(function() {
                self.savingView = false;
            });
        },

        updateView() {
            if (!this.activeViewId || this.updatingView || this.activeViewIsPreset) return;

            var self = this;
            this.updatingView = true;

            this.$root.makeApiRequest(function(jwtToken) {
                return axios.put('/api/v0/report_views/' + self.activeViewId, {
                    config: JSON.stringify(self.buildViewConfig()),
                }, {
                    headers: { Authorization: jwtToken },
                });
            }).then(function(response) {
                var updated = response.data.data;
                self.savedViews = self.savedViews.map(function(v) {
                    return v.id === self.activeViewId ? updated : v;
                });
                self.viewDirty = false;
                self.showAlertMessage('View saved', 'success');
            }).catch(function(error) {
                console.error('ReportTable update view error', error);
                self.showAlertMessage('Could not save changes. Please try again.', 'error');
            }).finally(function() {
                self.updatingView = false;
            });
        },

        togglePinView(view) {
            if (view.is_preset) return;
            var self    = this;
            var pinning = !view.is_pinned;
            this.$root.makeApiRequest(function(jwtToken) {
                return axios.put('/api/v0/report_views/' + view.id + '/pin', {
                    pinned: pinning,
                }, {
                    headers: { Authorization: jwtToken },
                });
            }).then(function() {
                self.savedViews = self.savedViews.map(function(v) {
                    return Object.assign({}, v, { is_pinned: pinning && v.id === view.id });
                });
                self.showAlertMessage(pinning ? 'Pinned as default view' : 'Unpinned', 'success');
            }).catch(function() {
                self.showAlertMessage('Could not update pin. Please try again.', 'error');
            });
        },

        deleteView(id) {
            if (typeof id === 'string' && id.indexOf('preset:') === 0) return;
            if (!window.confirm('Delete this view?')) return;

            var self = this;
            this.$root.makeApiRequest(function(jwtToken) {
                return axios.delete('/api/v0/report_views/' + id, {
                    headers: { Authorization: jwtToken },
                });
            }).then(function() {
                self.savedViews = self.savedViews.filter(function(v) { return v.id !== id; });
                if (self.activeViewId === id) {
                    self.activeViewId = null;
                    self.activeViewIsPreset = false;
                    self.viewDirty = false;
                }
                self.showAlertMessage('View deleted', 'success');
            }).catch(function(error) {
                console.error('ReportTable delete view error', error);
                self.showAlertMessage('Could not delete view. Please try again.', 'error');
            });
        },

        clearSelected() {
            this.selectedIds = [];
        },

        toggleSelectAll(checked) {
            var self = this;
            if (checked) {
                this.rows.forEach(function(row) {
                    if (self.selectedIds.indexOf(row.id) === -1) {
                        self.selectedIds.push(row.id);
                    }
                });
            } else {
                var pageIds = this.rows.map(function(r) { return r.id; });
                this.selectedIds = this.selectedIds.filter(function(id) {
                    return pageIds.indexOf(id) === -1;
                });
            }
        },

        toggleSelectRow(id, checked) {
            var idx = this.selectedIds.indexOf(id);
            if (checked && idx === -1) {
                this.selectedIds.push(id);
            } else if (!checked && idx !== -1) {
                this.selectedIds.splice(idx, 1);
            }
        },
    },
};
</script>

<style scoped>
.report-table {
    border: 1px solid #ddd;
    border-radius: 3px;
    background: #fff;
}

.report-table__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 15px;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
}

.report-table__count {
    font-size: 14px;
    font-weight: 600;
    color: #444;
}

.report-table__search-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 480px;
}

.report-table__search-icon {
    position: absolute;
    left: 10px;
    color: #aaa;
    font-size: 13px;
    pointer-events: none;
}

.report-table__search-input {
    width: 100%;
    height: 34px;
    padding: 0 30px 0 32px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
    color: #333;
    background: #fafafa;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
}

.report-table__search-input:focus {
    border-color: #3c8dbc;
    background: #fff;
}

.report-table__search-clear {
    position: absolute;
    right: 8px;
    color: #aaa;
    font-weight: bold;
    font-size: 16px;
    text-decoration: none;
    line-height: 1;
}

.report-table__search-clear:hover {
    color: #c0392b;
    text-decoration: none;
}

.report-table__search-count {
    position: absolute;
    right: 30px;
    font-size: 11px;
    color: #bbb;
    pointer-events: none;
    white-space: nowrap;
}

.report-table__search-group {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    margin-right: 12px;
}

.report-table__save-changes-btn {
    margin-left: 6px;
    flex-shrink: 0;
}

.report-table__tools {
    display: flex;
    align-items: center;
}

.report-table__body {
}

.report-table__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 8px 15px;
    border-top: 1px solid #ddd;
    background: #fff;
}

.report-table__footer--fixed {
    position: fixed;
    bottom: 0;
    z-index: 100;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
}

.report-table__footer-count {
    line-height: 32px;
}

.report-table__footer-nav {
    display: flex;
}

.report-table__footer-nav .pagination {
    flex-wrap: nowrap;
}

@media (max-width: 767px) {
    .report-table__footer {
        gap: 8px;
    }
    .report-table__footer-count {
        display: none;
    }
    .report-table__toolbar {
        flex-wrap: wrap;
        gap: 6px;
    }
    .report-table__search-group {
        flex: 0 0 100%;
        margin-right: 0;
    }
    .report-table__search-wrap {
        max-width: 100%;
    }
    .report-table__tools {
        flex-wrap: wrap;
        gap: 4px;
    }
    .report-table__btn-label {
        display: none;
    }
}

.report-table__footer-limit {
    display: flex;
    align-items: center;
}

.report-table__limit-btn {
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    color: #888;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
}

.report-table__limit-btn:hover {
    color: #333;
}

.report-table__limit-dropdown {
    position: absolute;
    bottom: calc(100% + 6px);
    right: 0;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    min-width: 100px;
    z-index: 200;
    padding: 4px 0;
}

.report-table__limit-option {
    display: block;
    padding: 6px 14px;
    font-size: 13px;
    color: #333;
    text-decoration: none;
    white-space: nowrap;
}

.report-table__limit-option:hover {
    background: #f5f5f5;
    text-decoration: none;
    color: #333;
}

.report-table__limit-option--active {
    font-weight: 600;
    color: #3c8dbc;
}

.report-table__th {
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.report-table__th:hover {
    background-color: #f5f5f5;
}

.report-table__sort-icon {
    font-size: 10px;
    margin-left: 4px;
    color: #ccc;
}

.report-table__sort-icon--active {
    color: #3c8dbc;
}

.report-table__filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 15px;
    background: #fafafa;
    border-bottom: 1px solid #f4f4f4;
}

.report-table__filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 12px;
    height: 26px;
    box-sizing: border-box;
}

.report-table__filter-chip--locked {
    background: #f9f9f9;
    border-color: #e0e0e0;
    cursor: default;
}

.report-table__filter-locked-value {
    color: #777;
    font-size: 11px;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.report-table__filter-label {
    font-weight: 600;
    color: #555;
    white-space: nowrap;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.report-table__op-label {
    font-size: 11px;
    color: #888;
    padding: 0 2px;
}

.report-table__op-select {
    border: none;
    outline: none;
    background: transparent;
    font-size: 11px;
    color: #888;
    padding: 0 2px;
    cursor: pointer;
    height: 20px;
    box-sizing: border-box;
}

.report-table__filter-input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 12px;
    padding: 0;
    min-width: 80px;
    max-width: 160px;
    height: 20px;
    box-sizing: border-box;
}

.report-table__filter-input--date {
    max-width: 130px;
}

.report-table__filter-input--number {
    max-width: 70px;
}

.report-table__filter-sep {
    color: #aaa;
    font-size: 12px;
}

.report-table__multi-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    font-weight: normal;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    margin: 0;
}

.report-table__tag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: #e8f4fd;
    border: 1px solid #b8ddf5;
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 11px;
    color: #2c6fa8;
    white-space: nowrap;
}

.report-table__tag-remove {
    color: #2c6fa8;
    text-decoration: none;
    font-weight: bold;
    opacity: 0.6;
    line-height: 1;
}

.report-table__tag-remove:hover {
    opacity: 1;
    text-decoration: none;
}

.report-table__multi-toggle {
    background: none;
    border: none;
    padding: 1px 3px;
    cursor: pointer;
    color: #666;
    font-size: 12px;
    line-height: 1;
}

.report-table__multi-toggle:hover {
    color: #333;
}

.report-table__filter-remove {
    color: #aaa;
    font-weight: bold;
    text-decoration: none;
    margin-left: 2px;
    line-height: 1;
}

.report-table__filter-remove:hover {
    color: #c0392b;
    text-decoration: none;
}

.report-table__clear-all {
    font-size: 11px;
    color: #aaa;
    text-decoration: none;
}

.report-table__clear-all:hover {
    color: #c0392b;
}
.report-table__filter-summary-wrap {
    position: relative;
    display: inline-block;
    margin-left: 8px;
}
.report-table__filter-summary-toggle {
    font-size: 11px;
    color: #888;
    text-decoration: none;
    border: 1px solid #ddd;
    border-radius: 10px;
    padding: 1px 8px;
    white-space: nowrap;
}
.report-table__filter-summary-toggle:hover,
.report-table__filter-summary-toggle--open {
    color: #337ab7;
    border-color: #337ab7;
    text-decoration: none;
}
.report-table__filter-summary {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 320px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    z-index: 200;
    padding: 10px 12px 12px;
    text-align: left;
    cursor: default;
}
.report-table__filter-summary-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    color: #333;
    padding-bottom: 8px;
    margin-bottom: 4px;
    border-bottom: 1px solid #eee;
}
.report-table__filter-summary-close {
    color: #bbb;
    text-decoration: none;
    font-size: 15px;
    line-height: 1;
}
.report-table__filter-summary-close:hover {
    color: #c0392b;
    text-decoration: none;
}
.report-table__filter-summary-step {
    padding: 6px 0 2px;
}
.report-table__filter-summary-step--stale {
    opacity: 0.45;
}
.report-table__filter-summary-line {
    display: flex;
    align-items: baseline;
    font-size: 12px;
    margin-bottom: 3px;
}
.report-table__filter-summary-label {
    flex: 1;
    color: #555;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 6px;
}
.report-table__filter-summary-drop {
    color: #c0392b;
    font-size: 11px;
    padding-right: 8px;
    white-space: nowrap;
}
.report-table__filter-summary-count {
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}
.report-table__filter-summary-track {
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
}
.report-table__filter-summary-fill {
    height: 100%;
    background: #337ab7;
    border-radius: 2px;
}
.report-table__filter-summary-note {
    font-size: 11px;
    color: #888;
    padding-top: 6px;
}

.report-table__col-count {
    font-size: 11px;
    color: #888;
    margin: 0 2px;
}

.report-table__picker-dropdown {
    display: block;
    min-width: 220px;
    padding: 0;
}

.report-table__picker-search-wrap {
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
}

.report-table__picker-search {
    width: 100%;
    height: 28px;
    padding: 0 8px;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 12px;
    outline: none;
    box-sizing: border-box;
}

.report-table__picker-search:focus {
    border-color: #3c8dbc;
}

.report-table__picker-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 320px;
    overflow-y: auto;
}

.report-table__picker-list .dropdown-header {
    background: #f4f6f8;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 5px 12px 4px;
    color: #888;
}

.report-table__filter-type-icon {
    width: 14px;
    margin-right: 6px;
    font-size: 11px;
    color: #aaa;
    text-align: center;
    flex-shrink: 0;
}

.report-table__picker-no-results {
    padding: 8px 16px;
    font-size: 12px;
    color: #aaa;
    font-style: italic;
}

.report-table__drag-handle {
    color: #ccc;
    margin-right: 6px;
    cursor: grab;
    font-size: 12px;
}

.report-table__drag-handle:active {
    cursor: grabbing;
}

.report-table__col-item--dragging {
    opacity: 0.4;
}

.report-table__col-item--drag-over > a {
    background: #eaf4fd;
}

.report-table__dropdown-item {
    display: block;
    padding: 4px 16px;
    color: #333;
    text-decoration: none;
    white-space: nowrap;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.report-table__dropdown-item:hover {
    background: #f5f5f5;
    text-decoration: none;
}

.report-table__checkbox-cell {
    width: 32px;
    text-align: center;
    padding: 8px 6px !important;
    vertical-align: middle;
}

.report-table__bulk-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 15px;
    background: #eaf4fd;
    border-bottom: 1px solid #c8e5f3;
    font-size: 13px;
}

.report-table__tabs {
    display: flex;
    align-items: flex-end;
    border-bottom: 2px solid #ddd;
    background: #f4f6f8;
    padding: 5px 15px 0;
    overflow-x: auto;
    flex-wrap: nowrap;
}

.report-table__tabs-right {
    margin-left: auto;
    flex-shrink: 0;
    align-self: center;
    padding-left: 12px;
}

.report-table__tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    font-size: 13px;
    color: #888;
    text-decoration: none;
    border: 1px solid #dde0e4;
    border-bottom: none;
    margin-bottom: -2px;
    white-space: nowrap;
    cursor: pointer;
    border-radius: 3px 3px 0 0;
    background: #edf0f3;
    line-height: 1.4;
    flex-shrink: 0;
}

.report-table__tab:hover {
    color: #444;
    background: #e4e8ec;
    border-color: #d5d9de;
    text-decoration: none;
}

.report-table__tab--active {
    color: #333;
    background: #fff;
    border-color: #ddd;
    border-bottom-color: #fff;
    border-top: 2px solid #3c8dbc;
    font-weight: 600;
    text-decoration: none !important;
}

.report-table__tab-pin {
    font-size: 10px;
    line-height: 1;
    opacity: 0;
    transition: opacity 0.1s;
    color: #bbb;
    transform: rotate(45deg);
    display: inline-block;
    text-decoration: none;
}

.report-table__tab-pin--active {
    opacity: 1 !important;
    color: #3c8dbc;
    transform: rotate(0deg);
}

.report-table__tab:hover .report-table__tab-pin,
.report-table__tab--active .report-table__tab-pin {
    opacity: 1;
}

.report-table__tab-pin:hover {
    color: #3c8dbc;
}

.report-table__tab-shared {
    font-size: 10px;
    color: #888;
    margin-left: 2px;
}

.report-table__share-label {
    font-size: 11px;
    color: #555;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    white-space: nowrap;
    cursor: pointer;
    font-weight: normal;
}

.report-table__tab-close {
    color: #bbb;
    font-weight: bold;
    font-size: 15px;
    line-height: 1;
    opacity: 0;
    transition: opacity 0.1s;
    text-decoration: none;
}

.report-table__tab:hover .report-table__tab-close,
.report-table__tab--active .report-table__tab-close {
    opacity: 1;
}

.report-table__tab-close:hover {
    color: #c0392b;
}

.report-table__new-view-btn {
    align-self: center;
    margin-left: 6px;
    padding: 3px 10px;
    font-size: 12px;
    color: #777;
    background: none;
    border: 1px dashed #bbb;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
}

.report-table__new-view-btn:hover {
    color: #3c8dbc;
    border-color: #3c8dbc;
    background: #f0f7fc;
}

.report-table__save-form {
    display: inline-flex;
    align-items: center;
    align-self: center;
    gap: 5px;
    margin-left: 6px;
    flex-shrink: 0;
}

.report-table__tab-name-input {
    border: 1px solid #ccc;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 12px;
    height: 24px;
    width: 140px;
    outline: none;
}

.report-table__tab-name-input:focus {
    border-color: #3c8dbc;
}

.report-table__tab-save-btn {
    background: #3c8dbc;
    color: #fff;
    border: none;
    border-radius: 3px;
    padding: 2px 8px;
    font-size: 12px;
    height: 24px;
    cursor: pointer;
}

.report-table__tab-save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.report-table__tab-cancel {
    color: #aaa;
    font-weight: bold;
    font-size: 15px;
    text-decoration: none;
    line-height: 1;
}

.report-table__tab-cancel:hover {
    color: #c0392b;
    text-decoration: none;
}

.report-table__tab-skeleton {
    display: inline-block;
    width: 80px;
    height: 32px;
    background: linear-gradient(90deg, #e2e5e9 25%, #ebeef1 50%, #e2e5e9 75%);
    background-size: 200% 100%;
    animation: report-skeleton-shimmer 1.4s infinite;
    border-radius: 3px 3px 0 0;
    margin-right: 4px;
    margin-bottom: -2px;
    flex-shrink: 0;
}

.report-table__tab-skeleton--sm {
    width: 55px;
}

.report-table__filter-chip-skeleton {
    display: inline-block;
    width: 140px;
    height: 26px;
    background: linear-gradient(90deg, #e2e5e9 25%, #ebeef1 50%, #e2e5e9 75%);
    background-size: 200% 100%;
    animation: report-skeleton-shimmer 1.4s infinite;
    border-radius: 3px;
    flex-shrink: 0;
}

.report-table__filter-chip-skeleton--sm {
    width: 100px;
}

@keyframes report-skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.report-table__tab--loaded {
    animation: report-tab-fadein 0.2s ease-out;
}

@keyframes report-tab-fadein {
    from { opacity: 0; transform: translateY(3px); }
    to   { opacity: 1; transform: translateY(0); }
}

</style>



// WEBPACK FOOTER //
// plugins/Reports/Assets/components/ReportTable.vue